import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { detectUserTimezone } from '../../utils/timezoneUtils';
import { CalendarIcon, UsersIcon, ClockIcon, DollarSignIcon, TrendingUpIcon, ArrowUpIcon, ArrowDownIcon, BookOpenIcon, ChevronRightIcon, XIcon, UserPlusIcon, ClipboardListIcon, MessageSquareIcon, FileTextIcon, ChevronDownIcon, ChevronUpIcon, CheckCircleIcon, AlertCircleIcon, MoreVerticalIcon, Edit2Icon, Trash2Icon, UserXIcon,
// Add this import for No-Show icon
LoaderIcon } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { AddStudentModal } from '../modals/AddStudentModal';
import { AddTaskModal } from '../AddTaskModal';
import { CreateNewLessonModal } from '../CreateNewLessonModal';
import { TaskDetailsPanel } from '../tasks/TaskDetailsPanel';
import { getTaskDisplayStatus } from '../tasks/taskUtils';
import { useTheme } from '../providers/ThemeProvider';
import { getTeacherStudents } from '../../utils/teacherUtils';
import { addStudentToTeacher } from '../../services/studentService';
import { useLessons } from '../../src/hooks/useLessons';
import { DeleteLessonModal } from '../DeleteLessonModal';
import { CancelLessonModal } from '../modals/CancelLessonModal';
import { LessonDetailsModal } from '../LessonDetailsModal';
import { JoinLessonPanel } from '../modals/JoinLessonPanel';
import { RecurringEditModal } from '../modals/RecurringEditModal';
import { updateRecurringLessons } from '../../src/services/lessonsService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { addTask, getTeacherTasks, Task, updateTask, deleteTask } from '../../services/taskService';
import { toast } from 'react-hot-toast';
import { ActionItemsSection } from '../dashboard/ActionItemsSection';
import { StudentsNeedingAttentionSection } from '../dashboard/StudentsNeedingAttentionSection';
import { checkAndCreateOverdueNotifications } from '../../services/notificationService';
import { isLessonOnline } from '../../utils/lessonHelpers';
import { serverTimestamp } from 'firebase/firestore';
import { getLessonDisplayStatus, getLessonStatusBadge } from '../../utils/lessonStatusHelpers';
import { AddAlertModal } from '../AddAlertModal';
import { createAlert } from '../../services/alertService';
import { WelcomeOnboardingModal } from '../modals/WelcomeOnboardingModal';
// Default timezone - could be replaced with user preferences in the future
const DEFAULT_TIMEZONE = 'America/Los_Angeles';
// Stat card component
const StatCard = ({
  icon,
  label,
  value,
  change,
  changeType,
  darkMode,
  onClick,
  clickable
}) => {
  return <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} stat-card-hover`} onClick={onClick} data-id="element-8152">
      <div className="flex justify-between items-start" data-id="element-8153">
        <div data-id="element-8154">
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8155">
            {label}
          </p>
          <h3 className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-8156">
            {value}
          </h3>
        </div>
        <div className={`p-3 rounded-full ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8157">
          {icon}
        </div>
      </div>
      {change && <div className="mt-4 flex items-center" data-id="element-8158">
          {changeType === 'increase' ? <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-8159" /> : <ArrowDownIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-8160" />}
          <span className={`text-sm ${changeType === 'increase' ? 'text-green-500' : 'text-red-500'}`} data-id="element-8161">
            {change}
          </span>
          <span className={`text-sm ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8162">
            vs last month
          </span>
        </div>}
    </div>;
};
// Quick action button component
const QuickActionButton = ({
  icon,
  label,
  onClick,
  darkMode
}) => {
  return <button onClick={onClick} className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all duration-300 hover:shadow-md hover:-translate-y-1 ${darkMode ? 'bg-navy-800 hover:bg-navy-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-800'} shadow-sm`} data-id="element-8163">
      <div className={`p-3 rounded-full mb-2 ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8164">
        {icon}
      </div>
      <span className="text-sm font-medium" data-id="element-8165">{label}</span>
    </button>;
};
// Chart card component
const ChartCard = ({
  title,
  subtitle,
  children,
  darkMode
}) => {
  return <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-8166">
      <div className="mb-6" data-id="element-8167">
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-8168">
          {title}
        </h3>
        {subtitle && <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8169">
            {subtitle}
          </p>}
      </div>
      {children}
    </div>;
};
// Upcoming lesson card component
const LessonCard = ({
  lesson,
  darkMode
}) => {
  return <div className={`p-4 rounded-lg border ${darkMode ? 'bg-navy-700/50 border-navy-600' : 'bg-gray-50 border-gray-200'} flex justify-between items-center mb-4`} data-id="element-8170">
      <div data-id="element-8171">
        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-8172">
          {lesson.student}
        </h4>
        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8173">
          {lesson.instrument} • {lesson.duration}
        </p>
      </div>
      <div className="text-right" data-id="element-8174">
        <p className={`font-medium ${lesson.isToday ? 'text-deep-pink' : darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-8175">
          {lesson.date}
        </p>
        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8176">
          {lesson.time}
        </p>
      </div>
      {lesson.isToday && isLessonOnline(lesson.location) && <Link to={`/student/lesson/${lesson.id}/join`} className="ml-4 px-4 py-2 bg-deep-pink hover:bg-burgundy text-white rounded-lg text-sm whitespace-nowrap" data-id="element-8177">
          Join
        </Link>}
    </div>;
};
export const TeacherDashboard = () => {
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  // Add loading state for student operations
  const [loading, setLoading] = useState(false);
  // Track whether onboarding should be suppressed (skipped or already completed)
  const onboardingSuppressedRef = useRef(false);
  // States for modals
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  // Add state for Create Lesson Modal
  const [isCreateLessonModalOpen, setIsCreateLessonModalOpen] = useState(false);
  // Add state for Add Alert Modal
  const [isAddAlertModalOpen, setIsAddAlertModalOpen] = useState(false);
  // New state for student count
  const [studentCount, setStudentCount] = useState(0);
  // New state for lessons
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [groupedLessons, setGroupedLessons] = useState({
    today: [],
    tomorrow: [],
    later: []
  });
  // New state for lessons this week count
  const [lessonsThisWeek, setLessonsThisWeek] = useState(0);
  // New state for tasks due this week count
  const [tasksDueThisWeek, setTasksDueThisWeek] = useState(0);
  // State for tracking which lesson's notes are expanded
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  // New state for tasks
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [expandedTaskSummaries, setExpandedTaskSummaries] = useState<Set<string>>(new Set());
  // State for task details panel
  const [selectedTaskForPanel, setSelectedTaskForPanel] = useState<Task | null>(null);
  const [isTaskPanelOpen, setIsTaskPanelOpen] = useState(false);
  const [isTaskEditMode, setIsTaskEditMode] = useState(false);
  // State for lesson actions menu
  const [openMenuLessonId, setOpenMenuLessonId] = useState<string | null>(null);
  const [lessonToDelete, setLessonToDelete] = useState<any | null>(null);
  const [isDeleteLessonModalOpen, setIsDeleteLessonModalOpen] = useState(false);
  const [lessonToCancel, setLessonToCancel] = useState<any | null>(null);
  const [isCancelLessonModalOpen, setIsCancelLessonModalOpen] = useState(false);
  // Add state for viewing lesson details
  const [viewingLesson, setViewingLesson] = useState<any | null>(null);
  const [showViewLessonModal, setShowViewLessonModal] = useState(false);
  // Recurring edit state
  const [isRecurringEditModalOpen, setIsRecurringEditModalOpen] = useState(false);
  const [pendingEditUpdates, setPendingEditUpdates] = useState<{
    lessonId: string;
    updates: any;
  } | null>(null);
  const [isRecurringEditing, setIsRecurringEditing] = useState(false);
  // State for Join Lesson Panel
  const [isJoinLessonPanelOpen, setIsJoinLessonPanelOpen] = useState(false);
  const [selectedLessonForJoin, setSelectedLessonForJoin] = useState<any | null>(null);
  // Add state for tracking lesson action loading
  const [updatingLessonId, setUpdatingLessonId] = useState<string | null>(null);
  // Calculate date range for Firestore subscription
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Start of current week (Sunday)
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  // End of next week (Saturday)
  const endOfNextWeek = new Date(startOfWeek);
  endOfNextWeek.setDate(startOfWeek.getDate() + 14);
  endOfNextWeek.setHours(23, 59, 59, 999);
  // Subscribe to lessons from Firestore
  const {
    lessons: firestoreLessons,
    deleteLesson,
    updateLesson
  } = useLessons({
    teacherId: currentUser?.uid || '',
    tz: DEFAULT_TIMEZONE,
    range: {
      fromISOUtc: startOfWeek.toISOString(),
      toISOUtc: endOfNextWeek.toISOString()
    }
  });
  // Check onboarding status from Firestore
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!currentUser?.uid) return;
      try {
        const teacherRef = doc(db, 'teachers', currentUser.uid);
        const teacherDoc = await getDoc(teacherRef);
        if (teacherDoc.exists()) {
          const data = teacherDoc.data();
          // Set onboardingSuppressedRef when onboarding is already completed or field doesn't exist
          if (data.onboardingCompleted === false) {
            setIsWelcomeModalOpen(true);
          } else {
            onboardingSuppressedRef.current = true;
          }
        } else {
          onboardingSuppressedRef.current = true;
        }
      } catch (error) {
        console.error('[TeacherDashboard] Error checking onboarding status:', error);
      }
    };
    checkOnboardingStatus();
  }, [currentUser?.uid]);
  // Auto-complete onboarding when teacher has both students and lessons
  useEffect(() => {
    const autoCompleteOnboarding = async () => {
      if (!currentUser?.uid) return;
      if (studentCount > 0 && firestoreLessons && firestoreLessons.length > 0) {
        try {
          const teacherRef = doc(db, 'teachers', currentUser.uid);
          const teacherDoc = await getDoc(teacherRef);
          if (teacherDoc.exists() && teacherDoc.data().onboardingCompleted === false) {
            await updateDoc(teacherRef, {
              onboardingCompleted: true
            });
            onboardingSuppressedRef.current = true;
            setIsWelcomeModalOpen(false);
          }
        } catch (error) {
          console.error('[TeacherDashboard] Error auto-completing onboarding:', error);
        }
      }
    };
    autoCompleteOnboarding();
  }, [currentUser?.uid, studentCount, firestoreLessons]);
  // Dismiss modal without persisting (X button / action buttons)
  const dismissOnboardingModal = () => {
    setIsWelcomeModalOpen(false);
  };
  // Skip onboarding permanently (Skip for now button)
  const skipOnboarding = async () => {
    setIsWelcomeModalOpen(false);
    onboardingSuppressedRef.current = true;
    if (!currentUser?.uid) return;
    try {
      const teacherRef = doc(db, 'teachers', currentUser.uid);
      await updateDoc(teacherRef, {
        onboardingCompleted: true
      });
    } catch (error) {
      console.error('[TeacherDashboard] Error skipping onboarding:', error);
    }
  };
  // Process Firestore lessons into grouped format
  useEffect(() => {
    if (!firestoreLessons || firestoreLessons.length === 0) {
      console.log('[TeacherDashboard] No Firestore lessons found');
      setUpcomingLessons([]);
      setGroupedLessons({
        today: [],
        tomorrow: [],
        later: []
      });
      setLessonsThisWeek(0);
      return;
    }
    console.log('[TeacherDashboard] Processing Firestore lessons:', {
      count: firestoreLessons.length,
      firstLesson: firestoreLessons[0]
    });
    // Get today's date with time set to beginning of day
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Get tomorrow's date
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    // Calculate start and end of current week (Sunday to Saturday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);
    endOfWeek.setHours(0, 0, 0, 0);
    // Calculate end of next week (14 days from start of week) for grouping
    const endOfNextWeek = new Date(startOfWeek);
    endOfNextWeek.setDate(startOfWeek.getDate() + 14);
    endOfNextWeek.setHours(0, 0, 0, 0);
    // Count lessons this week
    const weekLessons = firestoreLessons.filter(lesson => {
      const lessonDate = new Date(lesson.startAt);
      lessonDate.setHours(0, 0, 0, 0);
      return lessonDate >= startOfWeek && lessonDate < endOfWeek;
    });
    setLessonsThisWeek(weekLessons.length);
    // Filter and group lessons
    const todaysLessons = [];
    const tomorrowsLessons = [];
    const laterLessons = [];
    firestoreLessons.forEach(lesson => {
      // SKIP finalized lessons - they should not appear in Upcoming Lessons
      if (lesson.status === 'COMPLETED' || lesson.status === 'CANCELED') {
        return; // Skip this lesson
      }
      const lessonDate = new Date(lesson.startAt);
      lessonDate.setHours(0, 0, 0, 0);
      // Format time for display
      const startTime = new Date(lesson.startAt).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      const endTime = new Date(lesson.endAt).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      const displayTime = `${startTime} - ${endTime}`;
      const formattedLesson = {
        ...lesson,
        student: lesson.studentName || 'Student',
        time: displayTime,
        duration: `${lesson.durationMinutes} min`
      };
      if (lessonDate.getTime() === today.getTime()) {
        todaysLessons.push({
          ...formattedLesson,
          date: 'Today',
          isToday: true,
          isTomorrow: false,
          isThisWeek: false
        });
      } else if (lessonDate.getTime() === tomorrow.getTime()) {
        tomorrowsLessons.push({
          ...formattedLesson,
          date: 'Tomorrow',
          isToday: false,
          isTomorrow: true,
          isThisWeek: false
        });
      } else if (lessonDate >= startOfWeek && lessonDate < endOfNextWeek && lessonDate > tomorrow) {
        const dateStr = lessonDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });
        laterLessons.push({
          ...formattedLesson,
          date: dateStr,
          isToday: false,
          isTomorrow: false,
          isThisWeek: true
        });
      }
    });
    // Sort each group by time
    const sortByTime = (a, b) => {
      const timeA = new Date(a.startAt).getTime();
      const timeB = new Date(b.startAt).getTime();
      return timeA - timeB;
    };
    todaysLessons.sort(sortByTime);
    tomorrowsLessons.sort(sortByTime);
    laterLessons.sort(sortByTime);
    // Combine all lessons for total count
    const allUpcoming = [...todaysLessons, ...tomorrowsLessons, ...laterLessons];
    setUpcomingLessons(allUpcoming);
    setGroupedLessons({
      today: todaysLessons,
      tomorrow: tomorrowsLessons,
      later: laterLessons
    });
    console.log('[TeacherDashboard] Lessons grouped from Firestore:', {
      today: todaysLessons.length,
      tomorrow: tomorrowsLessons.length,
      later: laterLessons.length,
      total: allUpcoming.length
    });
  }, [firestoreLessons]);
  // Load tasks due this week from localStorage
  useEffect(() => {
    const loadTasksDueThisWeek = () => {
      try {
        const TASKS_STORAGE_KEY = 'mc_tasks_temp';
        const storedTasks = localStorage.getItem(TASKS_STORAGE_KEY);
        if (storedTasks) {
          const allTasks = JSON.parse(storedTasks);
          // Get today's date
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          // Calculate start and end of current week (Sunday to Saturday)
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 7);
          endOfWeek.setHours(0, 0, 0, 0);
          // Count tasks due this week (not completed)
          const weekTasks = allTasks.filter(task => {
            // Skip completed tasks
            if (task.status === 'completed') return false;
            const dueDateStr = task.deadline || task.dueDate || task.dueAt;
            const dueDate = dueDateStr ? new Date(dueDateStr) : null;
            if (!dueDate) {
              console.log('[TASK SUMMARY] Task has no due date:', task.id);
              return false;
            }
            const dueTime = dueDate.getTime();
            return dueDate >= startOfWeek && dueDate < endOfWeek;
          });
          setTasksDueThisWeek(weekTasks.length);
        } else {
          setTasksDueThisWeek(0);
        }
      } catch (error) {
        console.error('Error loading tasks due this week:', error);
        setTasksDueThisWeek(0);
      }
    };
    // Load tasks initially
    loadTasksDueThisWeek();
    // Listen for task updates
    const handleTaskUpdated = () => {
      loadTasksDueThisWeek();
    };
    window.addEventListener('taskCreated', handleTaskUpdated);
    window.addEventListener('taskUpdated', handleTaskUpdated);
    window.addEventListener('taskDeleted', handleTaskUpdated);
    return () => {
      window.removeEventListener('taskCreated', handleTaskUpdated);
      window.removeEventListener('taskUpdated', handleTaskUpdated);
      window.removeEventListener('taskDeleted', handleTaskUpdated);
    };
  }, []);
  // Load students from Firestore to get the count
  useEffect(() => {
    const loadStudentCount = async () => {
      if (!currentUser) {
        setStudentCount(0);
        return;
      }
      try {
        const students = await getTeacherStudents(currentUser.uid);
        // Count only active students
        const activeStudents = students.filter(student => !student.status || student.status === 'active' || student.status === 'trial' || student.status === 'pending');
        setStudentCount(activeStudents.length);
      } catch (error) {
        console.error('Error loading student count:', error);
        setStudentCount(0);
      }
    };
    loadStudentCount();
    // Listen for studentDeleted event
    const handleStudentDeleted = () => {
      loadStudentCount();
    };
    window.addEventListener('studentDeleted', handleStudentDeleted);
    return () => {
      window.removeEventListener('studentDeleted', handleStudentDeleted);
    };
  }, [currentUser]);
  // Load all tasks for the teacher
  useEffect(() => {
    const loadTasks = async () => {
      if (!currentUser?.uid) return;
      try {
        console.log('[TASK SUMMARY] Loading tasks for teacher:', currentUser.uid);
        const tasks = await getTeacherTasks(currentUser.uid);
        console.log('[TASK SUMMARY] Loaded tasks:', {
          count: tasks.length,
          tasks: tasks.map(t => ({
            id: t.id,
            title: t.title,
            studentId: t.studentId,
            dueDate: t.dueDate,
            deadline: t.deadline,
            status: t.status
          }))
        });
        setAllTasks(tasks);
        // Check for overdue tasks and create notifications
        await checkAndCreateOverdueNotifications(currentUser.uid, tasks).catch(err => console.error('[TASK SUMMARY] Error checking overdue tasks:', err));
      } catch (error) {
        console.error('Error loading tasks:', error);
      }
    };
    loadTasks();
    // Listen for task updates
    const handleTaskUpdated = () => {
      loadTasks();
    };
    window.addEventListener('taskCreated', handleTaskUpdated);
    window.addEventListener('taskUpdated', handleTaskUpdated);
    window.addEventListener('taskDeleted', handleTaskUpdated);
    return () => {
      window.removeEventListener('taskCreated', handleTaskUpdated);
      window.removeEventListener('taskUpdated', handleTaskUpdated);
      window.removeEventListener('taskDeleted', handleTaskUpdated);
    };
  }, [currentUser]);
  // Auto-detect and save timezone on first load if not set
  useEffect(() => {
    const autoDetectTimezone = async () => {
      if (!currentUser?.uid) return;
      try {
        const teacherRef = doc(db, 'teachers', currentUser.uid);
        const teacherSnap = await getDoc(teacherRef);
        if (teacherSnap.exists()) {
          const data = teacherSnap.data();
          // Only auto-detect if timezone is not set or is still the default
          if (!data.timeZone || !data.timezoneAutoDetected) {
            const detectedTimezone = detectUserTimezone();
            if (detectedTimezone) {
              console.log('[Dashboard] Auto-detecting timezone on first load:', detectedTimezone);
              await updateDoc(teacherRef, {
                timeZone: detectedTimezone,
                timezoneAutoDetected: true,
                updatedAt: new Date().toISOString()
              });
              console.log('[Dashboard] Timezone auto-detected and saved:', detectedTimezone);
            }
          }
        }
      } catch (error) {
        console.error('[Dashboard] Error auto-detecting timezone:', error);
        // Fail silently - don't disrupt user experience
      }
    };
    autoDetectTimezone();
  }, [currentUser]);
  // Sample data for student progress chart
  const studentProgressData = [{
    month: 'Jan',
    completed: 18,
    pending: 5,
    overdue: 2
  }, {
    month: 'Feb',
    completed: 22,
    pending: 7,
    overdue: 3
  }, {
    month: 'Mar',
    completed: 25,
    pending: 6,
    overdue: 1
  }, {
    month: 'Apr',
    completed: 30,
    pending: 8,
    overdue: 2
  }, {
    month: 'May',
    completed: 28,
    pending: 10,
    overdue: 4
  }, {
    month: 'Jun',
    completed: 32,
    pending: 7,
    overdue: 2
  }];
  // Sample data for top students progress
  const topStudentsData = [{
    name: 'Emma Johnson',
    progress: 95
  }, {
    name: 'Michael Chen',
    progress: 88
  }, {
    name: 'Sophia Williams',
    progress: 82
  }, {
    name: 'Aiden Rodriguez',
    progress: 78
  }, {
    name: 'Olivia Martinez',
    progress: 75
  }];
  // Custom tooltip for charts
  const CustomTooltip = ({
    active,
    payload,
    label,
    darkMode
  }) => {
    if (active && payload && payload.length) {
      return <div className={`p-3 rounded-lg shadow-md ${darkMode ? 'bg-navy-700 text-white' : 'bg-white text-gray-800'}`} data-id="element-8178">
          <p className="font-medium" data-id="element-8179">{label}</p>
          {payload.map((entry, index) => <p key={`item-${index}`} style={{
          color: entry.color
        }} data-id="element-8180">
              {entry.name}: {entry.value}
            </p>)}
        </div>;
    }
    return null;
  };
  // Custom tooltip for student progress
  const StudentProgressTooltip = ({
    active,
    payload,
    label,
    darkMode
  }) => {
    if (active && payload && payload.length) {
      return <div className={`p-3 rounded-lg shadow-md ${darkMode ? 'bg-navy-700 text-white' : 'bg-white text-gray-800'}`} data-id="element-8181">
          <p className="font-medium" data-id="element-8182">{label}</p>
          <p className="text-green-500" data-id="element-8183">Completed: {payload[0]?.value}</p>
          <p className="text-blue-500" data-id="element-8184">Pending: {payload[1]?.value}</p>
          <p className="text-red-500" data-id="element-8185">Overdue: {payload[2]?.value}</p>
        </div>;
    }
    return null;
  };
  // Helper function to toggle notes expansion
  const toggleNotes = (lessonId: string) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lessonId)) {
        newSet.delete(lessonId);
      } else {
        newSet.add(lessonId);
      }
      return newSet;
    });
  };
  // Helper function to truncate notes
  const truncateNotes = (notes: string, maxLength: number = 60) => {
    if (!notes || notes.length <= maxLength) return notes;
    return notes.slice(0, maxLength) + '...';
  };
  // Helper function to truncate task title
  const truncateTaskTitle = (title: string, maxLength: number = 30) => {
    if (!title || title.length <= maxLength) return title;
    return title.slice(0, maxLength) + '...';
  };
  // Helper function to get location display with icon
  const getLocationDisplay = (location?: string) => {
    if (!location) return null;
    // Check if it's an online lesson
    const isOnline = location.toLowerCase().includes('online') || location.toLowerCase().includes('zoom') || location.toLowerCase().includes('virtual');
    return <span className="flex items-center" data-id="element-8186">
        <span className="mr-1" data-id="element-8187">{isOnline ? '🌐' : '📍'}</span>
        {location}
      </span>;
  };
  // Helper function to get lesson type badge
  const getLessonTypeBadge = (lessonType?: string, darkMode?: boolean) => {
    if (!lessonType) return null;
    return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${darkMode ? 'bg-navy-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-8188">
        {lessonType}
      </span>;
  };
  // Helper function to toggle task summary expansion
  const toggleTaskSummary = (lessonId: string) => {
    setExpandedTaskSummaries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lessonId)) {
        newSet.delete(lessonId);
      } else {
        newSet.add(lessonId);
      }
      return newSet;
    });
  };
  // Helper function to get the task linked to a specific lesson
  const getTaskForLesson = (lessonId: string, studentId: string, lessonDate: Date) => {
    // Normalize lesson date to YYYY-MM-DD for comparison
    const lessonDateNormalized = new Date(lessonDate);
    lessonDateNormalized.setHours(0, 0, 0, 0);
    const lessonDateStr = lessonDateNormalized.toISOString().split('T')[0];
    console.log('[LESSON TASK DEBUG] ========== MATCHING TASK FOR LESSON ==========');
    console.log('[LESSON TASK DEBUG] Lesson Details:', {
      lessonId,
      studentId,
      lessonStartAt: lessonDate.toISOString(),
      lessonDateNormalized: lessonDateNormalized.toISOString(),
      lessonDateStr
    });
    console.log('[LESSON TASK DEBUG] Total tasks loaded:', allTasks.length);
    // Log all tasks for this student
    const studentTasks = allTasks.filter(t => t.studentId === studentId);
    console.log('[LESSON TASK DEBUG] Tasks for this student:', studentTasks.length);
    studentTasks.forEach(task => {
      const dueDateStr = task.deadline || task.dueDate || task.dueAt;
      let taskDateStr = 'NO_DATE';
      if (dueDateStr) {
        const taskDate = new Date(dueDateStr);
        taskDate.setHours(0, 0, 0, 0);
        taskDateStr = taskDate.toISOString().split('T')[0];
      }
      console.log('[LESSON TASK DEBUG] Candidate Task:', {
        taskId: task.id,
        taskTitle: task.title,
        studentId: task.studentId,
        dueDateRaw: dueDateStr,
        dueDateNormalized: taskDateStr,
        lessonId: task.lessonId,
        status: task.status,
        matchesDate: taskDateStr === lessonDateStr,
        matchesLessonId: task.lessonId === lessonId
      });
    });
    // Primary: Find task with matching lessonId
    const linkedTask = allTasks.find(task => task.lessonId === lessonId);
    if (linkedTask) {
      console.log('[LESSON TASK DEBUG] ✅ MATCH BY lessonId:', {
        taskId: linkedTask.id,
        taskTitle: linkedTask.title,
        lessonId: linkedTask.lessonId
      });
      return linkedTask;
    }
    // Fallback: Find task by exact date match ONLY (for tasks without lessonId)
    console.log('[LESSON TASK DEBUG] No lessonId match, trying exact-date fallback');
    // Find tasks for this student with exact same date (no lessonId)
    const exactDateTasks = allTasks.filter(task => {
      // Skip if task already has a lessonId (belongs to another lesson)
      if (task.lessonId) return false;
      // Must match student
      if (task.studentId !== studentId) return false;
      // Skip completed tasks
      if (task.status === 'completed') return false;
      // Check if due date matches exactly
      const dueDateStr = task.deadline || task.dueDate || task.dueAt;
      if (!dueDateStr) return false;
      const taskDate = new Date(dueDateStr);
      taskDate.setHours(0, 0, 0, 0);
      const taskDateStr = taskDate.toISOString().split('T')[0];
      return taskDateStr === lessonDateStr;
    });
    if (exactDateTasks.length > 0) {
      // Return first non-completed task
      const fallbackTask = exactDateTasks[0];
      console.log('[LESSON TASK DEBUG] ✅ MATCH BY exact date:', {
        taskId: fallbackTask.id,
        taskTitle: fallbackTask.title,
        dueDate: fallbackTask.deadline || fallbackTask.dueDate || fallbackTask.dueAt,
        matchedDateStr: lessonDateStr
      });
      return fallbackTask;
    }
    // PHASE 1 FIX: Name-based fallback for duplicate student records
    console.log('[LESSON TASK DEBUG] No studentId match, trying name-based fallback (duplicate student workaround)');
    // Get lesson student name from the lesson object
    // Note: We need to get this from the parent scope since it's not passed as a parameter
    // We'll need to check if studentName is available in the lesson data
    const lessonStudentName = firestoreLessons?.find(l => l.id === lessonId)?.studentName;
    if (!lessonStudentName) {
      console.log('[LESSON TASK DEBUG] ❌ Cannot use name fallback: lesson studentName not found');
      console.log('[LESSON TASK DEBUG] ================================================');
      return null;
    }
    const lessonStudentNameNormalized = lessonStudentName.toLowerCase().trim();
    console.log('[LESSON TASK DEBUG] Searching by student name:', lessonStudentNameNormalized);
    // Find tasks matching by name + date (ignore studentId mismatch)
    const nameMatchTasks = allTasks.filter(task => {
      // Skip if task already has a lessonId (belongs to another lesson)
      if (task.lessonId) return false;
      // Skip completed tasks
      if (task.status === 'completed') return false;
      // Match by student name (case-insensitive, trimmed)
      const taskStudentName = (task.studentName || '').toLowerCase().trim();
      if (!taskStudentName || taskStudentName !== lessonStudentNameNormalized) {
        return false;
      }
      // Match by exact date
      const dueDateStr = task.deadline || task.dueDate || task.dueAt;
      if (!dueDateStr) return false;
      const taskDate = new Date(dueDateStr);
      taskDate.setHours(0, 0, 0, 0);
      const taskDateStr = taskDate.toISOString().split('T')[0];
      return taskDateStr === lessonDateStr;
    });
    if (nameMatchTasks.length > 0) {
      const fallbackTask = nameMatchTasks[0];
      console.log('[LESSON TASK DEBUG] ⚠️ MATCH BY student name (FALLBACK - studentId mismatch detected):', {
        taskId: fallbackTask.id,
        taskTitle: fallbackTask.title,
        taskStudentId: fallbackTask.studentId,
        taskStudentName: fallbackTask.studentName,
        lessonStudentId: studentId,
        lessonStudentName: lessonStudentName,
        WARNING: 'DUPLICATE STUDENT RECORDS DETECTED - Data cleanup needed',
        NOTE: 'Task and lesson have different studentIds but same student name'
      });
      return fallbackTask;
    }
    console.log('[LESSON TASK DEBUG] ❌ NO MATCH FOUND (tried lessonId, exact date by studentId, and name fallback)');
    console.log('[LESSON TASK DEBUG] ================================================');
    return null;
  };
  // Backward compatibility wrapper for Task Summary section
  const getPrimaryTaskForLesson = (studentId: string, lessonDate: Date) => {
    // This function is kept for the Task Summary expandable section
    // It finds tasks near the lesson date (±14 days window)
    const tasks = getStudentTasksNearLesson(studentId, lessonDate);
    if (tasks.length === 0) return null;
    // Return the first pending or overdue task
    const primaryTask = tasks.find(t => t.status !== 'completed') || tasks[0];
    return primaryTask;
  };
  // Helper function to get tasks for a student near a lesson date
  const getStudentTasksNearLesson = (studentId: string, lessonDate: Date) => {
    if (!studentId || !allTasks.length) {
      console.log('[TASK SUMMARY] No tasks to filter:', {
        studentId,
        tasksCount: allTasks.length
      });
      return [];
    }
    console.log('[TASK SUMMARY] Filtering tasks for student:', {
      studentId,
      lessonDate: lessonDate.toISOString(),
      totalTasks: allTasks.length
    });
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const nowTime = now.getTime();
    const lessonDateNormalized = new Date(lessonDate);
    lessonDateNormalized.setHours(0, 0, 0, 0);
    const lessonTime = lessonDateNormalized.getTime();
    // Filter tasks for this student
    const filtered = allTasks.filter(task => {
      // Must match student
      if (task.studentId !== studentId) return false;
      // Get task due date - check all possible date fields
      const dueDateStr = task.deadline || task.dueDate || task.dueAt;
      if (!dueDateStr) {
        console.log('[TASK SUMMARY] Task has no due date:', task.id);
        return false;
      }
      const dueDate = new Date(dueDateStr);
      dueDate.setHours(0, 0, 0, 0);
      const dueTime = dueDate.getTime();
      // Include if not completed AND (overdue OR due within 14 days of lesson)
      if (task.status === 'completed') return false;
      // Include overdue tasks
      if (dueTime < nowTime) {
        console.log('[TASK SUMMARY] Including overdue task:', {
          taskId: task.id,
          title: task.title,
          dueDate: dueDate.toISOString()
        });
        return true;
      }
      // Include tasks due within 14 days of the lesson (before or after)
      const fourteenDaysInMs = 14 * 24 * 60 * 60 * 1000;
      const timeDiff = Math.abs(dueTime - lessonTime);
      const isRelevant = timeDiff <= fourteenDaysInMs;
      if (isRelevant) {
        console.log('[TASK SUMMARY] Including relevant task:', {
          taskId: task.id,
          title: task.title,
          dueDate: dueDate.toISOString(),
          daysDiff: Math.round(timeDiff / (24 * 60 * 60 * 1000))
        });
      }
      return isRelevant;
    });
    // Sort by due date (soonest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.deadline || a.dueDate || a.dueAt || 0);
      const dateB = new Date(b.deadline || b.dueDate || b.dueAt || 0);
      return dateA.getTime() - dateB.getTime();
    });
    console.log('[TASK SUMMARY] Filtered tasks result:', {
      studentId,
      filteredCount: filtered.length,
      tasks: filtered.map(t => ({
        id: t.id,
        title: t.title,
        dueDate: t.deadline || t.dueDate || t.dueAt,
        status: t.status
      }))
    });
    return filtered;
  };
  // Helper function to count pending and overdue tasks
  const countTasksByStatus = (tasks: Task[]) => {
    const now = new Date().getTime();
    let pending = 0;
    let overdue = 0;
    tasks.forEach(task => {
      if (task.status === 'completed') return;
      // Check both deadline and dueDate fields
      const dueDateStr = task.deadline || task.dueDate || task.dueAt;
      const dueDate = dueDateStr ? new Date(dueDateStr) : null;
      if (!dueDate) return;
      if (dueDate.getTime() < now) {
        overdue++;
      } else {
        pending++;
      }
    });
    console.log('[TASK SUMMARY] Task counts:', {
      pending,
      overdue,
      total: tasks.length
    });
    return {
      pending,
      overdue
    };
  };
  // Helper function to format task due date
  const formatTaskDueDate = (dueDate: string | null) => {
    if (!dueDate) return 'No due date';
    const date = new Date(dueDate);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const taskDate = new Date(date);
    taskDate.setHours(0, 0, 0, 0);
    const diffTime = taskDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays === -1) return 'Due yesterday';
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays <= 7) return `Due in ${diffDays} days`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  // Helper function to get status badge for tasks
  const getTaskStatusBadge = (task: Task, darkMode: boolean) => {
    const now = new Date().getTime();
    // Check both deadline and dueDate fields
    const dueDateStr = task.deadline || task.dueDate || task.dueAt;
    const dueDate = dueDateStr ? new Date(dueDateStr) : null;
    const isOverdue = dueDate && dueDate.getTime() < now && task.status !== 'completed';
    if (task.status === 'completed') {
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-8189">
          <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-8190" />
          Completed
        </span>;
    }
    if (isOverdue) {
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'}`} data-id="element-8191">
          <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-8192" />
          Overdue
        </span>;
    }
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`} data-id="element-8193">
        Pending
      </span>;
  };
  // Handle successful student addition
  const handleStudentAddSuccess = async newStudent => {
    if (!currentUser) {
      console.error('No current user found');
      toast.error('You must be logged in to add a student.');
      return;
    }
    try {
      setLoading(true);
      const studentData = {
        studentId: newStudent.studentId || `manual-${Date.now()}`,
        name: newStudent.name,
        firstName: newStudent.firstName || '',
        lastName: newStudent.lastName || '',
        email: newStudent.email || '',
        phone: newStudent.phone || '',
        instrument: newStudent.instrument || '',
        status: newStudent.status || 'active',
        notes: newStudent.notes || '',
        profilePicture: newStudent.profilePicture || ''
      };
      console.log('[DASHBOARD] Adding student to Firestore:', {
        teacherId: currentUser.uid,
        studentData
      });
      // Write to Firestore (same as Students page)
      await addStudentToTeacher(currentUser.uid, studentData);
      console.log('[DASHBOARD] Student added successfully to Firestore');
      // Update local student count
      setStudentCount(prevCount => prevCount + 1);
      // Close the modal
      setIsAddStudentModalOpen(false);
      toast.success('Student added successfully!');
      // Reopen onboarding modal to guide to next step (if not skipped/completed)
      if (!onboardingSuppressedRef.current) {
        setTimeout(() => {
          setIsWelcomeModalOpen(true);
        }, 600);
      }
    } catch (err) {
      console.error('[DASHBOARD] Error adding student:', err);
      toast.error(`Failed to add student: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  // Handle successful task addition
  const handleTaskAddSuccess = async newTask => {
    try {
      // Generate a unique ID for the task
      const taskId = 'task_' + Date.now().toString();
      // Add to Firestore
      try {
        await addTask(currentUser.uid, newTask.studentId, {
          title: newTask.title,
          studentId: newTask.studentId,
          studentName: newTask.studentName,
          dueDate: newTask.dueDate,
          notes: newTask.notes || '',
          status: newTask.status || 'pending',
          reference: newTask.reference || '',
          lessonId: newTask.lessonId || undefined
        });
        console.log('Task added to Firestore successfully!');
        toast.success('Task created successfully!');
      } catch (firestoreError) {
        console.error('Error adding task to Firestore:', firestoreError);
        toast.error('Failed to create task in database');
        // Fall back to localStorage on Firestore error
      }
      // Also save to localStorage as backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      let existingTasks = [];
      try {
        const storedTasks = localStorage.getItem(TASKS_STORAGE_KEY);
        if (storedTasks) {
          existingTasks = JSON.parse(storedTasks);
        }
      } catch (error) {
        console.error('Error loading tasks from localStorage:', error);
      }
      // Add the new task to localStorage
      const updatedTasks = [...existingTasks, {
        id: taskId,
        ...newTask
      }];
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('taskCreated'));
      // Close the modal
      setIsAddTaskModalOpen(false);
    } catch (err) {
      console.error('Error adding task:', err);
      toast.error('Failed to create task');
    }
  };
  // Quick action handlers
  const handleAddStudent = () => {
    // Open the AddStudentModal (same one used in TeacherMessages)
    setIsAddStudentModalOpen(true);
  };
  const handleScheduleLesson = () => {
    setIsCreateLessonModalOpen(true);
  };
  const handleCreateTask = () => {
    // Open the AddTaskModal (same one used in TeacherTasks)
    setIsAddTaskModalOpen(true);
  };
  const handleSendMessage = () => {
    // Navigate to teacher messages page
    navigate('/teacher-messages');
  };
  // Handler for Add Alert quick action
  const handleAddAlert = () => {
    setIsAddAlertModalOpen(true);
  };
  // Handler for saving alert from quick action
  const handleSaveAlert = async (alertData: {
    studentId: string;
    studentName: string;
    reason: string;
    details: string;
    practiceHours: number;
    alertType?: string;
    threshold?: number;
  }) => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }
    try {
      // Build alert data object, only including defined values
      // This prevents Firestore errors from undefined values
      const alertPayload: any = {
        studentId: alertData.studentId,
        studentName: alertData.studentName,
        reason: alertData.reason,
        details: alertData.details,
        practiceHours: alertData.practiceHours,
        status: 'active',
        priority: 'medium'
      };
      // Only include optional fields if they have values
      if (alertData.alertType) {
        alertPayload.alertType = alertData.alertType;
      }
      if (alertData.threshold !== undefined && alertData.threshold > 0) {
        alertPayload.threshold = alertData.threshold;
      }
      await createAlert(currentUser.uid, alertPayload);
      // Dispatch event so StudentsNeedingAttentionSection refreshes immediately
      window.dispatchEvent(new Event('alertCreated'));
      toast.success('Alert created successfully');
      setIsAddAlertModalOpen(false);
    } catch (error) {
      console.error('Error creating alert:', error);
      throw error;
    }
  };
  // Handler for when a lesson is successfully created
  const handleLessonCreated = async (lessonData: any) => {
    console.log('[TEACHER DASHBOARD] Lesson created successfully:', lessonData);
    toast.success('Lesson created successfully!');
    setIsCreateLessonModalOpen(false);
    // Mark onboarding as completed after first lesson is created (no reopen)
    if (!onboardingSuppressedRef.current && currentUser?.uid) {
      onboardingSuppressedRef.current = true;
      setIsWelcomeModalOpen(false);
      try {
        const teacherRef = doc(db, 'teachers', currentUser.uid);
        await updateDoc(teacherRef, {
          onboardingCompleted: true
        });
        console.log('[TEACHER DASHBOARD] Onboarding marked complete after lesson creation');
      } catch (error) {
        console.error('[TEACHER DASHBOARD] Error completing onboarding:', error);
      }
    }
  };
  // State for toggling between task overview and top students
  const [showTaskOverview, setShowTaskOverview] = useState(true);
  // Task panel handlers
  const openTaskPanel = (task: Task) => {
    setSelectedTaskForPanel(task);
    setIsTaskPanelOpen(true);
    setIsTaskEditMode(false);
  };
  // Lesson actions menu handlers
  const toggleLessonMenu = (lessonId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuLessonId(openMenuLessonId === lessonId ? null : lessonId);
  };
  const handleEditLesson = (lesson: any) => {
    setOpenMenuLessonId(null);
    // Open the lesson details modal directly instead of navigating to calendar
    setViewingLesson(lesson);
    setShowViewLessonModal(true);
  };
  const handleDeleteLessonClick = (lesson: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuLessonId(null);
    setLessonToDelete(lesson);
    setIsDeleteLessonModalOpen(true);
  };
  const handleCancelLessonClick = (lesson: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuLessonId(null);
    setLessonToCancel(lesson);
    setIsCancelLessonModalOpen(true);
  };
  const handleConfirmDeleteLesson = async () => {
    if (!lessonToDelete) return;
    try {
      await deleteLesson(lessonToDelete.id);
      toast.success('Lesson deleted successfully');
      setIsDeleteLessonModalOpen(false);
      setLessonToDelete(null);
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast.error('Failed to delete lesson');
    }
  };
  const handleConfirmCancelLesson = async (cancelReason: string, cancelReasonNote?: string) => {
    if (!lessonToCancel) return;
    try {
      const updates: any = {
        status: 'CANCELED',
        cancelReason: cancelReason,
        cancelledAt: new Date().toISOString()
      };
      // Add optional note if provided
      if (cancelReasonNote && cancelReasonNote.trim()) {
        updates.cancelReasonNote = cancelReasonNote.trim();
      }
      await updateLesson(lessonToCancel.id, updates);
      toast.success('Lesson cancelled successfully');
      setIsCancelLessonModalOpen(false);
      setLessonToCancel(null);
    } catch (error) {
      console.error('Error cancelling lesson:', error);
      toast.error('Failed to cancel lesson');
    }
  };
  // Handler for saving meeting link
  const handleSaveMeetingLink = async (lessonId: string, meetingLink: string) => {
    try {
      await updateLesson(lessonId, {
        meetingLink
      });
      toast.success('Meeting link saved successfully');
    } catch (error) {
      console.error('Error saving meeting link:', error);
      toast.error('Failed to save meeting link');
      throw error;
    }
  };
  // Helper function to convert lesson to LessonDetails format for the modal
  const convertLessonToDetails = (lesson: any) => {
    if (!lesson) return null;
    // Format date
    const startDate = new Date(lesson.startAt);
    const dateStr = startDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    return {
      id: lesson.id,
      title: lesson.title || `${lesson.instrument} Lesson`,
      studentName: lesson.studentName || 'Student',
      studentId: lesson.studentId,
      instrument: lesson.instrument || 'Music',
      date: dateStr,
      time: lesson.time || '',
      duration: lesson.duration || `${lesson.durationMinutes || 45} minutes`,
      location: lesson.location || 'Online',
      status: lesson.status || 'confirmed',
      notes: lesson.notes || '',
      timezone: DEFAULT_TIMEZONE,
      recurringGroupId: lesson.recurringGroupId || undefined
    };
  };
  // Recurring lesson EDIT helpers
  const fetchRecurringEditCount = async (scope: 'this' | 'future' | 'all'): Promise<number> => {
    if (scope === 'this') return 1;
    if (!currentUser?.uid || !viewingLesson) return 1;
    const recurringGroupId = viewingLesson.recurringGroupId;
    if (!recurringGroupId) return 1;
    try {
      const calendarRef = collection(db, 'teachers', currentUser.uid, 'calendar');
      const q = query(calendarRef, where('recurringGroupId', '==', recurringGroupId));
      const snapshot = await getDocs(q);
      if (scope === 'all') return snapshot.size;
      const selectedStartMs = new Date(viewingLesson.startAt).getTime();
      return snapshot.docs.filter(docSnap => {
        const data = docSnap.data();
        const docStartAt = data.startAt?.toDate ? data.startAt.toDate() : new Date(data.startAt);
        return docStartAt.getTime() >= selectedStartMs;
      }).length;
    } catch (error) {
      console.error('[RECURRING EDIT] fetchCount error:', error);
      return 1;
    }
  };
  const handleRecurringEdit = async (scope: 'this' | 'future' | 'all') => {
    if (!pendingEditUpdates || !currentUser?.uid || !viewingLesson) return;
    setIsRecurringEditing(true);
    try {
      const recurringGroupId = viewingLesson.recurringGroupId;
      const lessonStartAt = typeof viewingLesson.startAt === 'string' ? viewingLesson.startAt : viewingLesson.startAt?.toDate?.()?.toISOString() || new Date(viewingLesson.startAt).toISOString();
      const count = await updateRecurringLessons(currentUser.uid, pendingEditUpdates.lessonId, recurringGroupId, scope, pendingEditUpdates.updates, lessonStartAt);
      toast.success(`Updated ${count} lesson${count !== 1 ? 's' : ''}`);
      setIsRecurringEditModalOpen(false);
      setPendingEditUpdates(null);
      setShowViewLessonModal(false);
      setViewingLesson(null);
    } catch (error) {
      console.error('[RECURRING EDIT] handleRecurringEdit error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update lessons');
    } finally {
      setIsRecurringEditing(false);
    }
  };
  // Add handlers for lesson confirmation actions
  const handleMarkCompleted = async (lesson: any) => {
    if (!lesson?.id) return;
    setUpdatingLessonId(lesson.id);
    try {
      await updateLesson(lesson.id, {
        status: 'COMPLETED',
        completedAt: new Date().toISOString(),
        attended: true,
        cancelled: false
      });
      toast.success('Lesson marked as completed');
    } catch (error) {
      console.error('Error marking lesson as completed:', error);
      toast.error('Failed to update lesson');
    } finally {
      setUpdatingLessonId(null);
    }
  };
  const handleMarkNoShow = async (lesson: any) => {
    if (!lesson?.id) return;
    setUpdatingLessonId(lesson.id);
    try {
      await updateLesson(lesson.id, {
        status: 'COMPLETED',
        completedAt: new Date().toISOString(),
        attended: false,
        cancelled: false
      });
      toast.success('Lesson marked as no-show');
    } catch (error) {
      console.error('Error marking lesson as no-show:', error);
      toast.error('Failed to update lesson');
    } finally {
      setUpdatingLessonId(null);
    }
  };
  const handleMarkCancelled = async (lesson: any) => {
    if (!lesson?.id) return;
    setUpdatingLessonId(lesson.id);
    try {
      await updateLesson(lesson.id, {
        status: 'CANCELED',
        cancelled: true,
        cancelReason: 'Marked after lesson ended',
        cancelledAt: new Date().toISOString()
      });
      toast.success('Lesson marked as cancelled');
    } catch (error) {
      console.error('Error marking lesson as cancelled:', error);
      toast.error('Failed to update lesson');
    } finally {
      setUpdatingLessonId(null);
    }
  };
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuLessonId) {
        setOpenMenuLessonId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuLessonId]);
  const closeTaskPanel = () => {
    setIsTaskPanelOpen(false);
    setIsTaskEditMode(false);
    setTimeout(() => {
      setSelectedTaskForPanel(null);
    }, 300);
  };
  const handleTaskEditClick = () => {
    setIsTaskEditMode(true);
  };
  const handleTaskExitEditMode = () => {
    setIsTaskEditMode(false);
  };
  const handleTaskDelete = async (taskId: string) => {
    if (!currentUser?.uid || !selectedTaskForPanel?.studentId) {
      toast.error('Cannot delete task: missing information');
      return;
    }
    try {
      await deleteTask(currentUser.uid, selectedTaskForPanel.studentId, taskId);
      // Update local state
      const updatedTasks = allTasks.filter(task => task.id !== taskId);
      setAllTasks(updatedTasks);
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event
      window.dispatchEvent(new Event('taskDeleted'));
      toast.success('Task deleted successfully');
      closeTaskPanel();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };
  const handleTaskSave = async (taskId: string, updates: any) => {
    if (!currentUser?.uid || !selectedTaskForPanel?.studentId) {
      toast.error('Cannot update task: missing information');
      return;
    }
    try {
      const updateData: any = {
        title: updates.title,
        status: updates.status,
        notes: updates.notes || '',
        reference: updates.reference || ''
      };
      if (updates.dueAt) {
        updateData.dueDate = updates.dueAt;
      }
      if (updates.startDate) {
        updateData.startDate = updates.startDate;
      }
      await updateTask(currentUser.uid, selectedTaskForPanel.studentId, taskId, updateData);
      // Update local state
      const updatedTaskData = {
        ...selectedTaskForPanel,
        id: taskId,
        name: updates.title,
        title: updates.title,
        deadline: updates.dueAt || selectedTaskForPanel.deadline,
        dueDate: updates.dueAt || selectedTaskForPanel.dueDate,
        startDate: updates.startDate || selectedTaskForPanel.startDate,
        notes: updates.notes || '',
        status: updates.status,
        reference: updates.reference || ''
      };
      const updatedTasks = allTasks.map(task => task.id === taskId ? updatedTaskData : task);
      setAllTasks(updatedTasks);
      setSelectedTaskForPanel(updatedTaskData);
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event
      window.dispatchEvent(new Event('taskUpdated'));
      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };
  return <>
      <div className="mx-auto" data-id="element-8194">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6" data-id="element-8195">
          <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8196">
            Teacher Dashboard
          </h1>
        </div>

        {/* Quick Actions row */}
        <div className="mb-6" data-id="element-8197">
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8198">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-8199">
            <button onClick={handleAddStudent} className={`flex flex-col items-center justify-center p-4 rounded-xl shadow-sm ${isDark ? 'bg-navy-800 hover:bg-navy-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-800'}`} style={{
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden'
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = isDark ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.15)';
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateZ(0)';
            e.currentTarget.style.boxShadow = '';
          }} data-id="element-8200">
              <div className={`p-3 rounded-full mb-2 ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8201">
                <UserPlusIcon className="h-6 w-6" style={{
                color: '#3B82F6'
              }} data-id="element-8202" />
              </div>
              <span className="text-sm font-medium" data-id="element-8203">Add Student</span>
            </button>
            <button onClick={handleScheduleLesson} className={`flex flex-col items-center justify-center p-4 rounded-xl shadow-sm ${isDark ? 'bg-navy-800 hover:bg-navy-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-800'}`} style={{
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden'
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = isDark ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.15)';
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateZ(0)';
            e.currentTarget.style.boxShadow = '';
          }} data-id="element-8204">
              <div className={`p-3 rounded-full mb-2 ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8205">
                <CalendarIcon className="h-6 w-6" style={{
                color: '#3B82F6'
              }} data-id="element-8206" />
              </div>
              <span className="text-sm font-medium" data-id="element-8207">Schedule Lesson</span>
            </button>
            <button onClick={handleCreateTask} className={`flex flex-col items-center justify-center p-4 rounded-xl shadow-sm ${isDark ? 'bg-navy-800 hover:bg-navy-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-800'}`} style={{
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden'
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = isDark ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.15)';
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateZ(0)';
            e.currentTarget.style.boxShadow = '';
          }} data-id="element-8208">
              <div className={`p-3 rounded-full mb-2 ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8209">
                <ClipboardListIcon className="h-6 w-6" style={{
                color: '#22C55E'
              }} data-id="element-8210" />
              </div>
              <span className="text-sm font-medium" data-id="element-8211">Create Task</span>
            </button>
            <button onClick={handleAddAlert} className={`flex flex-col items-center justify-center p-4 rounded-xl shadow-sm ${isDark ? 'bg-navy-800 hover:bg-navy-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-800'}`} style={{
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden'
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = isDark ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.15)';
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateZ(0)';
            e.currentTarget.style.boxShadow = '';
          }} data-id="element-8212">
              <div className={`p-3 rounded-full mb-2 ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8213">
                <AlertCircleIcon className="h-6 w-6" style={{
                color: '#F97316'
              }} data-id="element-8214" />
              </div>
              <span className="text-sm font-medium" data-id="element-8215">Create Report</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 mb-6" data-id="element-8216">
          {/* Upcoming lessons section */}
          <div className={`rounded-xl shadow-sm ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-8217">
            <div className={`px-5 pt-5 pb-3 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`} data-id="element-8218">
              <h2 className={`text-xl font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8219">
                <CalendarIcon className="h-5 w-5 mr-2" style={{
                color: '#3B82F6'
              }} data-id="element-8220" />
                Upcoming Lessons
              </h2>
            </div>
            <div className="px-5 pt-3 pb-5" data-id="element-8221">
              {upcomingLessons.length > 0 ? <div className="space-y-6" data-id="element-8222">
                  {/* Today's lessons */}
                  {groupedLessons.today.length > 0 && <div data-id="element-8223">
                      <div className="flex items-center mb-4" data-id="element-8224">
                        <h3 className="text-base font-semibold text-green-500" data-id="element-8225">
                          Today
                        </h3>
                        <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-600'}`} data-id="element-8226">
                          {groupedLessons.today.length}
                        </span>
                      </div>
                      <div className="space-y-4" data-id="element-8227">
                        {groupedLessons.today.map(lesson => {
                    const lessonDate = new Date(lesson.startAt);
                    const studentTasks = getStudentTasksNearLesson(lesson.studentId, lessonDate);
                    const {
                      pending,
                      overdue
                    } = countTasksByStatus(studentTasks);
                    const hasRelevantTasks = pending > 0 || overdue > 0;
                    console.log('[TASK SUMMARY] Today lesson task summary:', {
                      lessonId: lesson.id,
                      studentId: lesson.studentId,
                      studentName: lesson.studentName,
                      tasksFound: studentTasks.length,
                      pending,
                      overdue,
                      hasRelevantTasks
                    });
                    return <div key={lesson.id} className={`quick-action-hover p-6 rounded-xl ${isDark ? 'bg-navy-700/70 shadow-lg shadow-green-500/10' : 'bg-white shadow-md shadow-green-500/5'} ${lesson.status === 'CANCELED' ? 'opacity-60' : ''}`} style={{
                      boxShadow: isDark ? '0 0 0 1px rgba(34, 197, 94, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)' : '0 0 0 1px rgba(34, 197, 94, 0.08), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                      transform: 'translateZ(0)',
                      willChange: 'transform',
                      backfaceVisibility: 'hidden'
                    }} onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = isDark ? '0 0 0 1px rgba(34, 197, 94, 0.25), 0 8px 16px -2px rgba(0, 0, 0, 0.5), 0 4px 8px -2px rgba(0, 0, 0, 0.3)' : '0 0 0 1px rgba(34, 197, 94, 0.15), 0 8px 16px -2px rgba(0, 0, 0, 0.15), 0 4px 8px -2px rgba(0, 0, 0, 0.08)';
                    }} onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateZ(0)';
                      e.currentTarget.style.boxShadow = isDark ? '0 0 0 1px rgba(34, 197, 94, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)' : '0 0 0 1px rgba(34, 197, 94, 0.08), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                    }} data-id="element-8228">
                              {/* Status Badge - Always Show */}
                              <div className="mb-3" data-id="element-8229">
                                {getLessonStatusBadge(getLessonDisplayStatus(lesson), isDark)}
                              </div>

                              {/* Quick Actions for Needs Confirmation */}
                              {getLessonDisplayStatus(lesson) === 'needs-confirmation' && <div className="mb-4 flex flex-wrap gap-2" data-id="element-8230">
                                  {console.log('[NEEDS CONFIRMATION] Rendering action buttons for lesson:', {
                          lessonId: lesson.id,
                          studentName: lesson.studentName,
                          displayStatus: getLessonDisplayStatus(lesson),
                          endAt: lesson.endAt
                        })}
                                  <button onClick={e => {
                          e.stopPropagation();
                          console.log('[NEEDS CONFIRMATION] Mark Completed clicked:', lesson.id);
                          handleMarkCompleted(lesson);
                        }} disabled={updatingLessonId === lesson.id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${updatingLessonId === lesson.id ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-green-900/30 text-green-300 border border-green-500/30 hover:bg-green-900/50' : 'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200'}`} data-id="element-8231">
                                    {updatingLessonId === lesson.id ? <LoaderIcon className="h-3.5 w-3.5 animate-spin" data-id="element-8232" /> : <CheckCircleIcon className="h-3.5 w-3.5" data-id="element-8233" />}
                                    Mark Completed
                                  </button>
                                  <button onClick={e => {
                          e.stopPropagation();
                          console.log('[NEEDS CONFIRMATION] Mark No-Show clicked:', lesson.id);
                          handleMarkNoShow(lesson);
                        }} disabled={updatingLessonId === lesson.id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${updatingLessonId === lesson.id ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-red-900/30 text-red-300 border border-red-500/30 hover:bg-red-900/50' : 'bg-red-100 text-red-800 border border-red-200 hover:bg-red-200'}`} data-id="element-8234">
                                    {updatingLessonId === lesson.id ? <LoaderIcon className="h-3.5 w-3.5 animate-spin" data-id="element-8235" /> : <UserXIcon className="h-3.5 w-3.5" data-id="element-8236" />}
                                    Mark No-Show
                                  </button>
                                  <button onClick={e => {
                          e.stopPropagation();
                          console.log('[NEEDS CONFIRMATION] Mark Cancelled clicked:', lesson.id);
                          handleMarkCancelled(lesson);
                        }} disabled={updatingLessonId === lesson.id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${updatingLessonId === lesson.id ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-900/50' : 'bg-yellow-100 text-yellow-800 border border-yellow-200 hover:bg-yellow-200'}`} data-id="element-8237">
                                    {updatingLessonId === lesson.id ? <LoaderIcon className="h-3.5 w-3.5 animate-spin" data-id="element-8238" /> : <XIcon className="h-3.5 w-3.5" data-id="element-8239" />}
                                    Mark Cancelled
                                  </button>
                                </div>}

                              <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr] gap-4 items-center" data-id="element-8240">
                                {/* Left: Student Info */}
                                <div className="flex-1" data-id="element-8241">
                                  <h4 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8242">
                                    {lesson.studentName}
                                  </h4>
                                  <div className={`flex flex-wrap items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8243">
                                    {lesson.instrument && <span data-id="element-8244">{lesson.instrument}</span>}
                                    {lesson.instrument && lesson.duration && <span data-id="element-8245">•</span>}
                                    {lesson.duration && <span data-id="element-8246">{lesson.duration}</span>}
                                    {(lesson.instrument || lesson.duration) && lesson.location && <span data-id="element-8247">•</span>}
                                    {lesson.location && getLocationDisplay(lesson.location)}
                                    {(lesson.instrument || lesson.duration || lesson.location) && lesson.lessonType && <span data-id="element-8248">•</span>}
                                    {lesson.lessonType && getLessonTypeBadge(lesson.lessonType, isDark)}
                                  </div>
                                </div>

                                {/* Middle: Assigned Task */}
                                <div className="flex-1 min-w-0" data-id="element-8249">
                                  <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8250">
                                    Assigned Task
                                  </p>
                                  {(() => {
                            const lessonDate = new Date(lesson.startAt);
                            console.log('[RENDER DEBUG] Today lesson rendering:', {
                              lessonId: lesson.id,
                              studentId: lesson.studentId,
                              studentName: lesson.studentName,
                              startAt: lesson.startAt
                            });
                            const primaryTask = getTaskForLesson(lesson.id, lesson.studentId, lessonDate);
                            console.log('[RENDER DEBUG] Today primaryTask result:', primaryTask ? {
                              id: primaryTask.id,
                              title: primaryTask.title
                            } : 'NULL');
                            if (primaryTask) {
                              return <div onClick={e => {
                                e.stopPropagation();
                                openTaskPanel(primaryTask);
                              }} className="cursor-pointer hover:opacity-80 transition-opacity mt-2" data-id="element-8251">
                                          <p className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-800'}`} title={primaryTask.title} data-id="element-8252">
                                            {truncateTaskTitle(primaryTask.title)}
                                          </p>
                                        </div>;
                            }
                            return <p className={`text-sm font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8253">
                                        —
                                      </p>;
                          })()}
                                </div>

                                {/* Right: Date/Time + Action + Menu */}
                                <div className="flex items-center gap-4 justify-end relative" data-id="element-8254">
                                  <div className="text-right" data-id="element-8255">
                                    <p className="text-lg font-semibold text-green-500 mb-2" data-id="element-8256">
                                      Today
                                    </p>
                                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8257">
                                      {lesson.time ? lesson.time.split(' - ')[0] : ''}
                                    </p>
                                  </div>
                                  {isLessonOnline(lesson.location) && lesson.status !== 'COMPLETED' && lesson.status !== 'CANCELED' && <button onClick={e => {
                            e.stopPropagation();
                            setSelectedLessonForJoin(lesson);
                            setIsJoinLessonPanelOpen(true);
                          }} className="px-4 py-2 bg-transparent border-2 border-green-500 text-green-500 hover:bg-green-500/10 rounded-lg text-sm font-medium whitespace-nowrap transition-colors" data-id="element-8258">
                                        Join Now
                                      </button>}

                                  {/* 3-dot menu */}
                                  <div className="relative" data-id="element-8259">
                                    <button onClick={e => toggleLessonMenu(lesson.id, e)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-600 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`} aria-label="Lesson actions" data-id="element-8260">
                                      <MoreVerticalIcon className="h-5 w-5" data-id="element-8261" />
                                    </button>

                                    {/* Dropdown menu */}
                                    {openMenuLessonId === lesson.id && <div className={`absolute right-0 bottom-full mb-1 w-56 rounded-lg shadow-lg z-50 ${isDark ? 'bg-navy-800 border border-navy-600' : 'bg-white border border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-8262">
                                        <button onClick={() => handleEditLesson(lesson)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-8263">
                                          <Edit2Icon className="h-4 w-4 mr-3" data-id="element-8264" />
                                          Edit Lesson
                                        </button>
                                        {lesson.status !== 'CANCELED' && lesson.status !== 'COMPLETED' && <button onClick={e => handleCancelLessonClick(lesson, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors text-yellow-500 ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} data-id="element-8265">
                                              <XIcon className="h-4 w-4 mr-3" data-id="element-8266" />
                                              Cancel Lesson
                                            </button>}
                                        {lesson.status === 'CANCELED' && <div className={`w-full flex items-center px-4 py-3 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} cursor-not-allowed`} data-id="element-8267">
                                            <XIcon className="h-4 w-4 mr-3" data-id="element-8268" />
                                            Already Cancelled
                                          </div>}
                                        <button onClick={e => handleDeleteLessonClick(lesson, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors text-red-500 ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} data-id="element-8269">
                                          <Trash2Icon className="h-4 w-4 mr-3" data-id="element-8270" />
                                          Delete Lesson
                                        </button>
                                      </div>}
                                  </div>
                                </div>
                              </div>
                              {/* Expandable Notes Section */}
                              {lesson.notes && <div className="mt-4 pt-4 border-t border-gray-200/10" data-id="element-8271">
                                  <button onClick={() => toggleNotes(lesson.id)} className={`w-full flex items-start gap-2 text-left transition-all ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-8272">
                                    <FileTextIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#10B981]" data-id="element-8273" />
                                    <div className="flex-1 min-w-0" data-id="element-8274">
                                      <div className="flex items-center justify-between gap-2" data-id="element-8275">
                                        <p className={`text-sm transition-all duration-300 ${expandedNotes.has(lesson.id) ? '' : 'line-clamp-2'}`} data-id="element-8276">
                                          {expandedNotes.has(lesson.id) ? lesson.notes : truncateNotes(lesson.notes)}
                                        </p>
                                        {expandedNotes.has(lesson.id) ? <ChevronUpIcon className="h-4 w-4 flex-shrink-0" data-id="element-8277" /> : <ChevronDownIcon className="h-4 w-4 flex-shrink-0" data-id="element-8278" />}
                                      </div>
                                    </div>
                                  </button>
                                </div>}
                              {/* Task Summary Section */}
                              {hasRelevantTasks && <div className={`mt-4 pt-4 border-t border-gray-200/10`} data-id="element-8279">
                                  <button onClick={() => toggleTaskSummary(lesson.id)} className={`w-full flex items-start gap-2 text-left transition-all ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-8280">
                                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{
                            color: '#3B82F6'
                          }} data-id="element-8281" />
                                    <div className="flex-1 min-w-0" data-id="element-8282">
                                      <div className="flex items-center justify-between gap-2" data-id="element-8283">
                                        <p className="text-sm font-medium" data-id="element-8284">
                                          Tasks:{' '}
                                          {pending > 0 && `${pending} pending`}
                                          {pending > 0 && overdue > 0 && ', '}
                                          {overdue > 0 && `${overdue} overdue`}
                                        </p>
                                        {expandedTaskSummaries.has(lesson.id) ? <ChevronUpIcon className="h-4 w-4 flex-shrink-0" style={{
                                color: '#3B82F6'
                              }} data-id="element-8285" /> : <ChevronDownIcon className="h-4 w-4 flex-shrink-0" style={{
                                color: '#3B82F6'
                              }} data-id="element-8286" />}
                                      </div>
                                    </div>
                                  </button>
                                  {/* Expanded Task List */}
                                  {expandedTaskSummaries.has(lesson.id) && <div className="mt-3 space-y-2 animate-in slide-in-from-top-2 duration-300" data-id="element-8287">
                                      {studentTasks.map(task => <div key={task.id} className={`flex items-center justify-between p-2 rounded ${isDark ? 'bg-navy-800/50' : 'bg-gray-50'}`} data-id="element-8288">
                                          <div className="flex-1 min-w-0" data-id="element-8289">
                                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'} truncate`} data-id="element-8290">
                                              {task.title}
                                            </p>
                                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8291">
                                              {formatTaskDueDate(task.deadline || task.dueDate)}
                                            </p>
                                          </div>
                                          <div className="ml-2 flex-shrink-0" data-id="element-8292">
                                            {getTaskStatusBadge(task, isDark)}
                                          </div>
                                        </div>)}
                                      <Link to={`/assigned-tasks?student=${lesson.studentId}`} className="inline-flex items-center text-sm text-deep-pink hover:text-burgundy font-medium mt-2" data-id="element-8293">
                                        View All Tasks
                                        <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-8294" />
                                      </Link>
                                    </div>}
                                </div>}
                            </div>;
                  })}
                      </div>
                    </div>}
                  {/* Tomorrow's lessons */}
                  {groupedLessons.tomorrow.length > 0 && <div data-id="element-8295">
                      <div className="flex items-center mb-4" data-id="element-8296">
                        <h3 className="text-base font-semibold text-green-500" data-id="element-8297">
                          Tomorrow
                        </h3>
                        <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-600'}`} data-id="element-8298">
                          {groupedLessons.tomorrow.length}
                        </span>
                      </div>
                      <div className="space-y-4" data-id="element-8299">
                        {groupedLessons.tomorrow.map(lesson => {
                    const lessonDate = new Date(lesson.startAt);
                    const studentTasks = getStudentTasksNearLesson(lesson.studentId, lessonDate);
                    const {
                      pending,
                      overdue
                    } = countTasksByStatus(studentTasks);
                    const hasRelevantTasks = pending > 0 || overdue > 0;
                    return <div key={lesson.id} className={`quick-action-hover p-6 rounded-xl ${isDark ? 'bg-navy-700/70 shadow-lg shadow-green-500/10' : 'bg-white shadow-md shadow-green-500/5'} ${lesson.status === 'CANCELED' ? 'opacity-60' : ''}`} style={{
                      boxShadow: isDark ? '0 0 0 1px rgba(34, 197, 94, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)' : '0 0 0 1px rgba(34, 197, 94, 0.08), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                      transform: 'translateZ(0)',
                      willChange: 'transform',
                      backfaceVisibility: 'hidden'
                    }} onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = isDark ? '0 0 0 1px rgba(34, 197, 94, 0.25), 0 8px 16px -2px rgba(0, 0, 0, 0.5), 0 4px 8px -2px rgba(0, 0, 0, 0.3)' : '0 0 0 1px rgba(34, 197, 94, 0.15), 0 8px 16px -2px rgba(0, 0, 0, 0.15), 0 4px 8px -2px rgba(0, 0, 0, 0.08)';
                    }} onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateZ(0)';
                      e.currentTarget.style.boxShadow = isDark ? '0 0 0 1px rgba(34, 197, 94, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)' : '0 0 0 1px rgba(34, 197, 94, 0.08), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                    }} data-id="element-8300">
                              {/* Status Badge - Always Show */}
                              <div className="mb-3" data-id="element-8301">
                                {getLessonStatusBadge(getLessonDisplayStatus(lesson), isDark)}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr] gap-4 items-center" data-id="element-8302">
                                {/* Left: Student Info */}
                                <div className="flex-1" data-id="element-8303">
                                  <h4 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8304">
                                    {lesson.studentName}
                                  </h4>
                                  <div className={`flex flex-wrap items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8305">
                                    {lesson.instrument && <span data-id="element-8306">{lesson.instrument}</span>}
                                    {lesson.instrument && lesson.duration && <span data-id="element-8307">•</span>}
                                    {lesson.duration && <span data-id="element-8308">{lesson.duration}</span>}
                                    {(lesson.instrument || lesson.duration) && lesson.location && <span data-id="element-8309">•</span>}
                                    {lesson.location && getLocationDisplay(lesson.location)}
                                    {(lesson.instrument || lesson.duration || lesson.location) && lesson.lessonType && <span data-id="element-8310">•</span>}
                                    {lesson.lessonType && getLessonTypeBadge(lesson.lessonType, isDark)}
                                  </div>
                                </div>

                                {/* Middle: Assigned Task */}
                                <div className="flex-1 min-w-0" data-id="element-8311">
                                  <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8312">
                                    Assigned Task
                                  </p>
                                  {(() => {
                            const lessonDate = new Date(lesson.startAt);
                            console.log('[RENDER DEBUG] Tomorrow lesson rendering:', {
                              lessonId: lesson.id,
                              studentId: lesson.studentId,
                              studentName: lesson.studentName,
                              startAt: lesson.startAt
                            });
                            const primaryTask = getTaskForLesson(lesson.id, lesson.studentId, lessonDate);
                            console.log('[RENDER DEBUG] Tomorrow primaryTask result:', primaryTask ? {
                              id: primaryTask.id,
                              title: primaryTask.title
                            } : 'NULL');
                            if (primaryTask) {
                              return <div onClick={e => {
                                e.stopPropagation();
                                openTaskPanel(primaryTask);
                              }} className="cursor-pointer hover:opacity-80 transition-opacity" data-id="element-8313">
                                          <p className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-800'}`} title={primaryTask.title} data-id="element-8314">
                                            {truncateTaskTitle(primaryTask.title)}
                                          </p>
                                        </div>;
                            }
                            return <p className={`text-sm font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8315">
                                        —
                                      </p>;
                          })()}
                                </div>

                                {/* Right: Date/Time + Menu */}
                                <div className="flex items-center gap-4 justify-end relative" data-id="element-8316">
                                  <div className="text-right" data-id="element-8317">
                                    <p className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8318">
                                      Tomorrow
                                    </p>
                                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8319">
                                      {lesson.time ? lesson.time.split(' - ')[0] : ''}
                                    </p>
                                  </div>

                                  {/* 3-dot menu */}
                                  <div className="relative" data-id="element-8320">
                                    <button onClick={e => toggleLessonMenu(lesson.id, e)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-600 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`} aria-label="Lesson actions" data-id="element-8321">
                                      <MoreVerticalIcon className="h-5 w-5" data-id="element-8322" />
                                    </button>

                                    {/* Dropdown menu */}
                                    {openMenuLessonId === lesson.id && <div className={`absolute right-0 bottom-full mb-1 w-48 rounded-lg shadow-lg z-50 ${isDark ? 'bg-navy-800 border border-navy-600' : 'bg-white border border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-8323">
                                        <button onClick={() => handleEditLesson(lesson)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-8324">
                                          <Edit2Icon className="h-4 w-4 mr-3" data-id="element-8325" />
                                          Edit Lesson
                                        </button>
                                        {lesson.status !== 'CANCELED' && lesson.status !== 'COMPLETED' && <button onClick={e => handleCancelLessonClick(lesson, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors text-yellow-500 ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} data-id="element-8326">
                                              <XIcon className="h-4 w-4 mr-3" data-id="element-8327" />
                                              Cancel Lesson
                                            </button>}
                                        {lesson.status === 'CANCELED' && <div className={`w-full flex items-center px-4 py-3 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} cursor-not-allowed`} data-id="element-8328">
                                            <XIcon className="h-4 w-4 mr-3" data-id="element-8329" />
                                            Already Cancelled
                                          </div>}
                                        <button onClick={e => handleDeleteLessonClick(lesson, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors text-red-500 ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} data-id="element-8330">
                                          <Trash2Icon className="h-4 w-4 mr-3" data-id="element-8331" />
                                          Delete Lesson
                                        </button>
                                      </div>}
                                  </div>
                                </div>
                              </div>
                              {/* Expandable Notes Section */}
                              {lesson.notes && <div className="mt-4 pt-4 border-t border-gray-200/10" data-id="element-8332">
                                  <button onClick={() => toggleNotes(lesson.id)} className={`w-full flex items-start gap-2 text-left transition-all ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-8333">
                                    <FileTextIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#10B981]" data-id="element-8334" />
                                    <div className="flex-1 min-w-0" data-id="element-8335">
                                      <div className="flex items-center justify-between gap-2" data-id="element-8336">
                                        <p className={`text-sm transition-all duration-300 ${expandedNotes.has(lesson.id) ? '' : 'line-clamp-2'}`} data-id="element-8337">
                                          {expandedNotes.has(lesson.id) ? lesson.notes : truncateNotes(lesson.notes)}
                                        </p>
                                        {expandedNotes.has(lesson.id) ? <ChevronUpIcon className="h-4 w-4 flex-shrink-0" data-id="element-8338" /> : <ChevronDownIcon className="h-4 w-4 flex-shrink-0" data-id="element-8339" />}
                                      </div>
                                    </div>
                                  </button>
                                </div>}
                              {/* Task Summary Section */}
                              {hasRelevantTasks && <div className={`mt-4 pt-4 border-t border-gray-200/10`} data-id="element-8340">
                                  <button onClick={() => toggleTaskSummary(lesson.id)} className={`w-full flex items-start gap-2 text-left transition-all ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-8341">
                                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-8342" />
                                    <div className="flex-1 min-w-0" data-id="element-8343">
                                      <div className="flex items-center justify-between gap-2" data-id="element-8344">
                                        <p className="text-sm font-medium" data-id="element-8345">
                                          Tasks:{' '}
                                          {pending > 0 && `${pending} pending`}
                                          {pending > 0 && overdue > 0 && ', '}
                                          {overdue > 0 && `${overdue} overdue`}
                                        </p>
                                        {expandedTaskSummaries.has(lesson.id) ? <ChevronUpIcon className="h-4 w-4 flex-shrink-0" data-id="element-8346" /> : <ChevronDownIcon className="h-4 w-4 flex-shrink-0" data-id="element-8347" />}
                                      </div>
                                    </div>
                                  </button>
                                  {/* Expanded Task List */}
                                  {expandedTaskSummaries.has(lesson.id) && <div className="mt-3 space-y-2 animate-in slide-in-from-top-2 duration-300" data-id="element-8348">
                                      {studentTasks.map(task => <div key={task.id} className={`flex items-center justify-between p-2 rounded ${isDark ? 'bg-navy-800/50' : 'bg-gray-50'}`} data-id="element-8349">
                                          <div className="flex-1 min-w-0" data-id="element-8350">
                                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'} truncate`} data-id="element-8351">
                                              {task.title}
                                            </p>
                                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8352">
                                              {formatTaskDueDate(task.deadline || task.dueDate)}
                                            </p>
                                          </div>
                                          <div className="ml-2 flex-shrink-0" data-id="element-8353">
                                            {getTaskStatusBadge(task, isDark)}
                                          </div>
                                        </div>)}
                                      <Link to={`/assigned-tasks?student=${lesson.studentId}`} className="inline-flex items-center text-sm text-deep-pink hover:text-burgundy font-medium mt-2" data-id="element-8354">
                                        View All Tasks
                                        <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-8355" />
                                      </Link>
                                    </div>}
                                </div>}
                            </div>;
                  })}
                      </div>
                    </div>}
                  {/* Later lessons (next 14 days) */}
                  {groupedLessons.later.length > 0 && <div data-id="element-8356">
                      <div className="space-y-4" data-id="element-8357">
                        {groupedLessons.later.map(lesson => {
                    const lessonDate = new Date(lesson.startAt);
                    const studentTasks = getStudentTasksNearLesson(lesson.studentId, lessonDate);
                    const {
                      pending,
                      overdue
                    } = countTasksByStatus(studentTasks);
                    const hasRelevantTasks = pending > 0 || overdue > 0;
                    return <div key={lesson.id} className={`p-6 rounded-xl ${isDark ? 'bg-navy-700/70 shadow-lg' : 'bg-white shadow-md'} ${lesson.status === 'CANCELED' ? 'opacity-60' : ''}`} style={{
                      boxShadow: isDark ? '0 0 0 1px rgba(75, 85, 99, 0.3), 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)' : '0 0 0 1px rgba(209, 213, 219, 0.5), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                      transform: 'translateZ(0)',
                      willChange: 'transform',
                      backfaceVisibility: 'hidden'
                    }} onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = isDark ? '0 0 0 1px rgba(75, 85, 99, 0.4), 0 8px 16px -2px rgba(0, 0, 0, 0.5), 0 4px 8px -2px rgba(0, 0, 0, 0.3)' : '0 0 0 1px rgba(209, 213, 219, 0.7), 0 8px 16px -2px rgba(0, 0, 0, 0.15), 0 4px 8px -2px rgba(0, 0, 0, 0.08)';
                    }} onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateZ(0)';
                      e.currentTarget.style.boxShadow = isDark ? '0 0 0 1px rgba(75, 85, 99, 0.3), 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)' : '0 0 0 1px rgba(209, 213, 219, 0.5), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                    }} data-id="element-8358">
                              {/* Status Badge - Always Show */}
                              <div className="mb-3" data-id="element-8359">
                                {getLessonStatusBadge(getLessonDisplayStatus(lesson), isDark)}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr] gap-4 items-center" data-id="element-8360">
                                {/* Left: Student Info */}
                                <div className="flex-1" data-id="element-8361">
                                  <h4 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8362">
                                    {lesson.studentName}
                                  </h4>
                                  <div className={`flex flex-wrap items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8363">
                                    {lesson.instrument && <span data-id="element-8364">{lesson.instrument}</span>}
                                    {lesson.instrument && lesson.duration && <span data-id="element-8365">•</span>}
                                    {lesson.duration && <span data-id="element-8366">{lesson.duration}</span>}
                                    {(lesson.instrument || lesson.duration) && lesson.location && <span data-id="element-8367">•</span>}
                                    {lesson.location && getLocationDisplay(lesson.location)}
                                    {(lesson.instrument || lesson.duration || lesson.location) && lesson.lessonType && <span data-id="element-8368">•</span>}
                                    {lesson.lessonType && getLessonTypeBadge(lesson.lessonType, isDark)}
                                  </div>
                                </div>

                                {/* Middle: Assigned Task */}
                                <div className="flex-1 min-w-0" data-id="element-8369">
                                  <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8370">
                                    Assigned Task
                                  </p>
                                  {(() => {
                            const lessonDate = new Date(lesson.startAt);
                            console.log('[RENDER DEBUG] Later lesson rendering:', {
                              lessonId: lesson.id,
                              studentId: lesson.studentId,
                              studentName: lesson.studentName,
                              startAt: lesson.startAt
                            });
                            const primaryTask = getTaskForLesson(lesson.id, lesson.studentId, lessonDate);
                            console.log('[RENDER DEBUG] Later primaryTask result:', primaryTask ? {
                              id: primaryTask.id,
                              title: primaryTask.title
                            } : 'NULL');
                            if (primaryTask) {
                              return <div onClick={e => {
                                e.stopPropagation();
                                openTaskPanel(primaryTask);
                              }} className="cursor-pointer hover:opacity-80 transition-opacity" data-id="element-8371">
                                          <p className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-800'}`} title={primaryTask.title} data-id="element-8372">
                                            {truncateTaskTitle(primaryTask.title)}
                                          </p>
                                        </div>;
                            }
                            return <p className={`text-sm font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8373">
                                        —
                                      </p>;
                          })()}
                                </div>

                                {/* Right: Date/Time + Menu */}
                                <div className="flex items-center gap-4 justify-end relative" data-id="element-8374">
                                  <div className="text-right" data-id="element-8375">
                                    <p className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-8376">
                                      {lesson.date}
                                    </p>
                                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-8377">
                                      {lesson.time ? lesson.time.split(' - ')[0] : ''}
                                    </p>
                                  </div>

                                  {/* 3-dot menu */}
                                  <div className="relative" data-id="element-8378">
                                    <button onClick={e => toggleLessonMenu(lesson.id, e)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-600 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`} aria-label="Lesson actions" data-id="element-8379">
                                      <MoreVerticalIcon className="h-5 w-5" data-id="element-8380" />
                                    </button>

                                    {/* Dropdown menu */}
                                    {openMenuLessonId === lesson.id && <div className={`absolute right-0 bottom-full mb-1 w-56 rounded-lg shadow-lg z-50 ${isDark ? 'bg-navy-800 border border-navy-600' : 'bg-white border border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-8381">
                                        <button onClick={() => handleEditLesson(lesson)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-8382">
                                          <Edit2Icon className="h-4 w-4 mr-3" data-id="element-8383" />
                                          Edit Lesson
                                        </button>
                                        {lesson.status !== 'CANCELED' && lesson.status !== 'COMPLETED' && <button onClick={e => handleCancelLessonClick(lesson, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors text-yellow-500 ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} data-id="element-8384">
                                              <XIcon className="h-4 w-4 mr-3" data-id="element-8385" />
                                              Cancel Lesson
                                            </button>}
                                        {lesson.status === 'CANCELED' && <div className={`w-full flex items-center px-4 py-3 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} cursor-not-allowed`} data-id="element-8386">
                                            <XIcon className="h-4 w-4 mr-3" data-id="element-8387" />
                                            Already Cancelled
                                          </div>}
                                        <button onClick={e => handleDeleteLessonClick(lesson, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors text-red-500 ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} data-id="element-8388">
                                          <Trash2Icon className="h-4 w-4 mr-3" data-id="element-8389" />
                                          Delete Lesson
                                        </button>
                                      </div>}
                                  </div>
                                </div>
                              </div>
                              {/* Expandable Notes Section */}
                              {lesson.notes && <div className="mt-4 pt-4 border-t border-gray-200/10" data-id="element-8390">
                                  <button onClick={() => toggleNotes(lesson.id)} className={`w-full flex items-start gap-2 text-left transition-all ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-8391">
                                    <FileTextIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#10B981]" data-id="element-8392" />
                                    <div className="flex-1 min-w-0" data-id="element-8393">
                                      <div className="flex items-center justify-between gap-2" data-id="element-8394">
                                        <p className={`text-sm transition-all duration-300 ${expandedNotes.has(lesson.id) ? '' : 'line-clamp-2'}`} data-id="element-8395">
                                          {expandedNotes.has(lesson.id) ? lesson.notes : truncateNotes(lesson.notes)}
                                        </p>
                                        {expandedNotes.has(lesson.id) ? <ChevronUpIcon className="h-4 w-4 flex-shrink-0" data-id="element-8396" /> : <ChevronDownIcon className="h-4 w-4 flex-shrink-0" data-id="element-8397" />}
                                      </div>
                                    </div>
                                  </button>
                                </div>}
                              {/* Task Summary Section */}
                              {hasRelevantTasks && <div className={`mt-4 pt-4 border-t border-gray-200/10`} data-id="element-8398">
                                  <button onClick={() => toggleTaskSummary(lesson.id)} className={`w-full flex items-start gap-2 text-left transition-all ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-8399">
                                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-8400" />
                                    <div className="flex-1 min-w-0" data-id="element-8401">
                                      <div className="flex items-center justify-between gap-2" data-id="element-8402">
                                        <p className="text-sm font-medium" data-id="element-8403">
                                          Tasks:{' '}
                                          {pending > 0 && `${pending} pending`}
                                          {pending > 0 && overdue > 0 && ', '}
                                          {overdue > 0 && `${overdue} overdue`}
                                        </p>
                                        {expandedTaskSummaries.has(lesson.id) ? <ChevronUpIcon className="h-4 w-4 flex-shrink-0" data-id="element-8404" /> : <ChevronDownIcon className="h-4 w-4 flex-shrink-0" data-id="element-8405" />}
                                      </div>
                                    </div>
                                  </button>
                                  {/* Expanded Task List */}
                                  {expandedTaskSummaries.has(lesson.id) && <div className="mt-3 space-y-2 animate-in slide-in-from-top-2 duration-300" data-id="element-8406">
                                      {studentTasks.map(task => <div key={task.id} className={`flex items-center justify-between p-2 rounded ${isDark ? 'bg-navy-800/50' : 'bg-gray-50'}`} data-id="element-8407">
                                          <div className="flex-1 min-w-0" data-id="element-8408">
                                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'} truncate`} data-id="element-8409">
                                              {task.title}
                                            </p>
                                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8410">
                                              {formatTaskDueDate(task.deadline || task.dueDate)}
                                            </p>
                                          </div>
                                          <div className="ml-2 flex-shrink-0" data-id="element-8411">
                                            {getTaskStatusBadge(task, isDark)}
                                          </div>
                                        </div>)}
                                      <Link to={`/assigned-tasks?student=${lesson.studentId}`} className="inline-flex items-center text-sm text-deep-pink hover:text-burgundy font-medium mt-2" data-id="element-8412">
                                        View All Tasks
                                        <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-8413" />
                                      </Link>
                                    </div>}
                                </div>}
                            </div>;
                  })}
                      </div>
                    </div>}
                </div> : <p className={`text-center py-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8414">
                  No upcoming lessons scheduled
                </p>}
            </div>
          </div>
        </div>

        {/* Action Items and Students Needing Attention - Side by Side on Desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 mt-6 lg:mt-0" data-id="element-8415">
          <ActionItemsSection data-id="element-8416" />
          <StudentsNeedingAttentionSection data-id="element-8417" />
        </div>
      </div>

      {/* Add Student Modal */}
      <AddStudentModal isOpen={isAddStudentModalOpen} onClose={() => setIsAddStudentModalOpen(false)} student={selectedStudent} darkMode={isDark} onSuccess={handleStudentAddSuccess} data-id="element-8418" />
      {/* Add Task Modal */}
      {isAddTaskModalOpen && <AddTaskModal isOpen={isAddTaskModalOpen} onClose={() => setIsAddTaskModalOpen(false)} onAddTask={handleTaskAddSuccess} darkMode={isDark} teacherId={currentUser?.uid} data-id="element-8419" />}
      {/* Add Create Lesson Modal */}
      <CreateNewLessonModal isOpen={isCreateLessonModalOpen} onClose={() => setIsCreateLessonModalOpen(false)} onLessonAdded={handleLessonCreated} darkMode={isDark} initialDate="" initialTime="" data-id="element-8420" />

      {/* Add Alert Modal */}
      <AddAlertModal isOpen={isAddAlertModalOpen} onClose={() => setIsAddAlertModalOpen(false)} onSave={handleSaveAlert} darkMode={isDark} data-id="element-8421" />

      {/* Welcome Onboarding Modal */}
      <WelcomeOnboardingModal isOpen={isWelcomeModalOpen} onClose={dismissOnboardingModal} onSkip={skipOnboarding} onAddStudent={() => setIsAddStudentModalOpen(true)} onScheduleLesson={() => setIsCreateLessonModalOpen(true)} darkMode={isDark} hasStudents={studentCount > 0} hasLessons={firestoreLessons && firestoreLessons.length > 0} data-id="element-8422" />

      {/* Task Details Panel */}
      <TaskDetailsPanel open={isTaskPanelOpen} task={selectedTaskForPanel} onClose={closeTaskPanel} onEditClick={handleTaskEditClick} onExitEditMode={handleTaskExitEditMode} onDeleteClick={handleTaskDelete} onSaveEdit={handleTaskSave} darkMode={isDark} isEditMode={isTaskEditMode} data-id="element-8423" />

      {/* Delete Lesson Confirmation Modal */}
      <DeleteLessonModal isOpen={isDeleteLessonModalOpen} onClose={() => {
      setIsDeleteLessonModalOpen(false);
      setLessonToDelete(null);
    }} onConfirm={handleConfirmDeleteLesson} lessonTitle={lessonToDelete?.title || 'Lesson'} studentName={lessonToDelete?.studentName} darkMode={isDark} data-id="element-8424" />

      {/* Cancel Lesson Confirmation Modal */}
      <CancelLessonModal isOpen={isCancelLessonModalOpen} onClose={() => {
      setIsCancelLessonModalOpen(false);
      setLessonToCancel(null);
    }} onConfirm={handleConfirmCancelLesson} lessonTitle={lessonToCancel?.title || 'Lesson'} studentName={lessonToCancel?.studentName} darkMode={isDark} data-id="element-8425" />

      {/* Lesson Details Modal */}
      <LessonDetailsModal isOpen={showViewLessonModal} lesson={convertLessonToDetails(viewingLesson)} onClose={() => {
      setShowViewLessonModal(false);
      setViewingLesson(null);
    }} darkMode={isDark} onUpdateLesson={async (lessonId, updates) => {
      try {
        // Check if this is a recurring lesson
        const recurringGroupId = viewingLesson?.recurringGroupId;
        if (recurringGroupId) {
          // Recurring lesson — store pending updates and open scope modal
          console.log('[DASHBOARD] Recurring lesson detected, opening edit scope modal');
          setPendingEditUpdates({
            lessonId: String(lessonId),
            updates
          });
          setIsRecurringEditModalOpen(true);
          return {
            deferred: true
          }; // Signal to LessonDetailsModal to skip toast/mode change
        }
        await updateLesson(lessonId, updates);
        // Close the modal (toast handled by LessonDetailsModal)
        setShowViewLessonModal(false);
        setViewingLesson(null);
      } catch (error) {
        console.error('Error updating lesson:', error);
        toast.error('Failed to update lesson');
        throw error;
      }
    }} onDeleteLesson={async lesson => {
      try {
        await deleteLesson(lesson.id);
        toast.success('Lesson deleted successfully');
        setShowViewLessonModal(false);
        setViewingLesson(null);
      } catch (error) {
        console.error('Error deleting lesson:', error);
        toast.error('Failed to delete lesson');
      }
    }} data-id="element-8426" />

      {/* Recurring Edit Modal */}
      <RecurringEditModal isOpen={isRecurringEditModalOpen} onClose={() => {
      setIsRecurringEditModalOpen(false);
      setPendingEditUpdates(null);
    }} onConfirm={handleRecurringEdit} darkMode={isDark} lessonTitle={viewingLesson?.title || 'Lesson'} studentName={viewingLesson?.studentName || 'Student'} isUpdating={isRecurringEditing} fetchCount={fetchRecurringEditCount} data-id="element-8427" />

      {/* Join Lesson Panel */}
      <JoinLessonPanel isOpen={isJoinLessonPanelOpen} onClose={() => {
      setIsJoinLessonPanelOpen(false);
      setSelectedLessonForJoin(null);
    }} lesson={selectedLessonForJoin ? {
      id: selectedLessonForJoin.id,
      studentName: selectedLessonForJoin.studentName,
      meetingLink: selectedLessonForJoin.meetingLink
    } : null} onSaveMeetingLink={handleSaveMeetingLink} darkMode={isDark} data-id="element-8428" />
    </>;
};